import styled, { css } from 'styled-components'
import { COLORS } from 'consts/colors'
import { DURATIONS, EASING, RADII, SHADOWS } from 'styles/consts'

/**
 * The floating surface the overlay action buttons sit on: ONE white chip with a
 * hairline border and a soft shadow, holding borderless icon buttons. Shared by
 * the top-right `ActionsRow` (Resize / Enhance / Delete) and the top-left
 * `UndoSlot`, so both overlay slots read as the same control.
 *
 * The buttons themselves must stay borderless/transparent — the chrome belongs
 * to this chip. (Before, the row itself carried a full button's styling — a
 * fixed 24×24 box with its own border and background — which squeezed every
 * action into one button-sized square and pushed the icons past the preview.)
 */
export const overlayActionsChip = css`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADII.field};
  background-color: ${COLORS.surface};
  box-shadow: ${SHADOWS.card};
`

// Shared look of the icon buttons inside the chip (Resize / Enhance / Delete /
// Undo). Positioning is owned by the chip, not the button, so every action
// button shares this exact appearance.
const actionButtonBase = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  cursor: pointer;
  transition: background-color ${DURATIONS.fast} ${EASING.out};

  &:hover {
    background-color: ${COLORS.fill};
  }

  &:active {
    background-color: ${COLORS.fillStrong};
  }
`

export const ActionButton = styled.button<{ $isActive?: boolean }>`
  ${actionButtonBase}
  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: ${COLORS.primaryTint};

      svg path {
        fill: ${COLORS.primary};
      }
    `}
`
